import { useState } from 'react'

import DownloadIcon from '@heroicons/react/outline/DownloadIcon'
import EyeIcon from '@heroicons/react/outline/EyeIcon'
import XIcon from '@heroicons/react/outline/XIcon'
import clsx from 'clsx'

import { File } from '@tribeplatform/gql-client/types'
import {
  MediaModal,
  PREVIEW_SUPPORTED_EXTENSION,
  Slide,
} from '@tribeplatform/react-sdk/components'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { Attachment, AttachmentsProps } from '../../@types'
import { icons } from '../icons'
import { AttachmentName } from './AttachmentName'

const staticProps = {
  spinnerStyles: { borderTopColor: 'transparent' },
}

const extensionIconMap = {
  jpeg: 'jpg',
  jfif: 'jpg',
  pptx: 'ppt',
}

const getExtensionIconName = (extension: Attachment['extension']) => {
  // For unknown file extensions apply "unknown" icon
  if (!extension) return 'unknown'

  // If we have a map for this file extension, show the mapped icon
  if (extensionIconMap[extension]) return extensionIconMap[extension]

  // Otherwise use the extension name as it is
  return extension
}

const hasPreview = (attachment: File) => {
  const extension = attachment?.extension?.toLowerCase()
  return PREVIEW_SUPPORTED_EXTENSION.includes(extension)
}

export const Attachments = ({
  attachments,
  onAttachmentDelete,
  isReadOnly,
}: AttachmentsProps) => {
  const [modalVisible, setModalVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const slides: Slide[] = attachments.filter(hasPreview)
  const onPreview = (attachment: File) => {
    setActiveIndex(slides.findIndex(slide => slide.id === attachment.id))
    setModalVisible(true)
  }

  return (
    <>
      <div>
        {attachments.map(attachment => {
          const { id, name, downloadUrl, isLoading } = attachment
          const extension = attachment?.extension?.toLowerCase()

          const canBePreviewed = hasPreview(attachment)

          const IconComponent =
            icons[getExtensionIconName(extension)] || icons.unknown

          return (
            <div
              key={id}
              className={clsx(
                'flex max-w-full overflow-hidden items-center justify-between space-x-3 bg-surface-50 px-6 py-4 rounded-md border border-basicSurface-300/25 mt-4 ignore-typography cursor-default',
                canBePreviewed && 'cursor-pointer',
              )}
              onClick={() => {
                if (!canBePreviewed) return
                onPreview(attachment)
              }}
            >
              <IconComponent className="flex-shrink-0" width={36} height={36} />
              <AttachmentName {...attachment} />
              {isReadOnly ? (
                <>
                  {canBePreviewed && (
                    <Tooltip>
                      <Tooltip.Trigger>
                        <EyeIcon
                          onClick={() => onPreview(attachment)}
                          className="cursor-pointer x-6 h-6 text-basicSurface-300 hover:text-basicSurface-500"
                        />
                      </Tooltip.Trigger>
                      <Tooltip.Panel>Preview</Tooltip.Panel>
                    </Tooltip>
                  )}
                  <Tooltip>
                    <Tooltip.Trigger>
                      <a
                        href={downloadUrl}
                        download={name}
                        onClick={e => {
                          e.stopPropagation()
                        }}
                      >
                        <DownloadIcon className="cursor-pointer x-6 h-6 text-basicSurface-300 hover:text-basicSurface-500" />
                      </a>
                    </Tooltip.Trigger>
                    <Tooltip.Panel>Download</Tooltip.Panel>
                  </Tooltip>
                </>
              ) : (
                <>
                  {isLoading && (
                    <div
                      style={staticProps.spinnerStyles}
                      className="w-5 h-5 border border-actionPrimary-800 rounded-full animate-spin"
                    />
                  )}
                  <Tooltip>
                    <Tooltip.Trigger>
                      <XIcon
                        onClick={e => {
                          e.stopPropagation()
                          onAttachmentDelete?.(attachment)
                        }}
                        className=" text-basicSurface-300 hover:text-basicSurface-500 cursor-pointer x-6 h-6"
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Panel>Delete</Tooltip.Panel>
                  </Tooltip>
                </>
              )}
            </div>
          )
        })}
      </div>
      {slides?.length > 0 && (
        <MediaModal
          slides={slides}
          isVisible={modalVisible}
          onClose={() => setModalVisible(false)}
          activeMediaIndex={activeIndex}
        />
      )}
    </>
  )
}
